import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Rating,
  Snackbar,
  Alert,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import {
  Close,
  QrCodeScanner,
  CameraAlt,
  PhotoLibrary,
  Refresh,
  CheckCircle,
  Info,
  AutoAwesome,
  ArrowForwardIos,
  ErrorOutline,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { createBottleWithImage } from '../../store/slices/bottlesSlice';
import { AppLayout } from '../../components/common/AppLayout';
import { BottleType, CreateBottleDto } from '../../../domain/entities';
import { ROUTES, BOTTLE_TYPES_LABELS } from '../../../core/config/constants';
import { Html5Qrcode } from "html5-qrcode";


export const AddBottlePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector((state) => state.bottles);

  const [formData, setFormData] = useState({
    reference: '',
    type: '' as BottleType | '',
    name: '',
    year: '',
    description: '',
    isGift: false,
    isPrivate: false,
  });
  const [rating, setRating] = useState<number>(0);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const scannerRef = useRef<HTMLDivElement>(null);
  const qrScannerRef = useRef<Html5Qrcode | null>(null);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (qrScannerRef.current) {
        stopScanning();
      }
    };
  }, []);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user types
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.reference) {
      errors.reference = 'Veuillez scanner un Tag QR pour continuer';
    }
    if (!formData.type) {
      errors.type = 'Le type de vin est requis';
    }
    if (!formData.name) {
      errors.name = 'Le nom est requis';
    }
    if (formData.year) {
      const year = parseInt(formData.year);
      if (isNaN(year) || year < 1800 || year > new Date().getFullYear() + 10) {
        errors.year = 'Année invalide';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleScanQRCode = async () => {
    try {
      // Vérifier si la permission API est supportée
      if ('permissions' in navigator) {
        const permissionResult = await navigator.permissions.query({ name: 'camera' as PermissionName });
        
        if (permissionResult.state === 'denied') {
          showCameraPermissionDialog();
          return;
        }
      }

      // Vérifier si getUserMedia est supporté
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Votre navigateur ne supporte pas l\'accès à la caméra.');
        return;
      }

      // Essayer d'obtenir l'accès à la caméra
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        // Fermer immédiatement le stream car nous allons utiliser Html5Qrcode
        stream.getTracks().forEach(track => track.stop());
      } catch (mediaError) {
        if (mediaError instanceof DOMException) {
          if (mediaError.name === 'NotAllowedError') {
            showCameraPermissionDialog();
          } else if (mediaError.name === 'NotFoundError') {
            setError('Aucune caméra trouvée sur cet appareil.');
          } else if (mediaError.name === 'NotReadableError') {
            setError('Caméra en cours d\'utilisation par une autre application. Fermez les autres onglets utilisant la caméra.');
          } else if (mediaError.name === 'OverconstrainedError') {
            setError('Les contraintes caméra ne peuvent pas être satisfaites.');
          } else {
            setError('Erreur d\'accès à la caméra. Vérifiez que votre connexion est sécurisée (HTTPS).');
          }
        } else {
          setError('Erreur inconnue lors de l\'accès à la caméra.');
        }
        return;
      }

      setIsScanning(true);
      
      // Attendre que le DOM soit mis à jour
      setTimeout(async () => {
        try {
          if (!qrScannerRef.current) {
            qrScannerRef.current = new Html5Qrcode("qr-scanner");
          }

          await qrScannerRef.current.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        (decodedText) => {
          handleInputChange('reference', decodedText);
          setSuccessMessage(`Tag scanné: ${decodedText}`);
          stopScanning();
        },
        (errorMessage) => {
          // Ignore scanning errors as they're frequent during scanning
          console.log('QR scan attempt:', errorMessage);
        }
      );
        } catch (err) {
          console.error('Scanner error:', err);
          setError('Erreur lors du démarrage du scanner. Veuillez réessayer.');
          setIsScanning(false);
        }
      }, 100); // Délai de 100ms pour laisser le DOM se mettre à jour
    } catch (err) {
      console.error('Scanner error:', err);
      setError('Erreur lors du démarrage du scanner. Veuillez réessayer.');
      setIsScanning(false);
    }
  };

  const stopScanning = async () => {
    try {
      if (qrScannerRef.current) {
        const scanner = qrScannerRef.current;
        qrScannerRef.current = null;
        
        // Vérifier si le scanner est en cours d'exécution
        if (scanner.isScanning) {
          await scanner.stop();
        }
        
        // Clear the scanner
        await scanner.clear();
      }
    } catch (err) {
      console.error('Error stopping scanner:', err);
    } finally {
      setIsScanning(false);
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('L\'image ne doit pas dépasser 5MB');
        return;
      }

      setSelectedImage(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setError('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    const bottleData: CreateBottleDto = {
      name: formData.name,
      type: formData.type as BottleType,
      year: formData.year ? parseInt(formData.year) : undefined,
      description: formData.description || undefined,
      rating,
      isGift: formData.isGift,
      isPrivate: formData.isPrivate,
    };

    try {
      await dispatch(createBottleWithImage({ 
        bottle: bottleData, 
        image: selectedImage || undefined,
        reference: formData.reference 
      })).unwrap();
      setSuccessMessage('Bouteille ajoutée avec succès !');
      setTimeout(() => {
        navigate(ROUTES.HOME);
      }, 1500);
    } catch (err: any) {
      if (err.message?.includes('TAG_NOT_RECOGNIZED')) {
        showErrorDialog('Tag QR non reconnu', 'Le Tag QR n\'est pas reconnu par Vinosk. Veuillez recommencer.');
      } else if (err.message?.includes('EXIST_ALREADY')) {
        showErrorDialog('Tag déjà utilisé', 'Ce Tag QR a déjà été utilisé pour créer une bouteille. Veuillez scanner un nouveau tag.');
      } else {
        setError(err.message || 'Erreur lors de l\'ajout de la bouteille');
      }
    }
  };

  const showErrorDialog = (title: string, message: string) => {
    setError(`${title}: ${message}`);
  };

  const showCameraPermissionDialog = () => {
    const isHttps = window.location.protocol === 'https:';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    let instructions = '';
    if (!isHttps && !isLocalhost) {
      instructions = 'Les autorisations caméra nécessitent une connexion sécurisée (HTTPS). Veuillez accéder au site via HTTPS.';
    } else {
      const userAgent = navigator.userAgent;
      if (userAgent.includes('Chrome')) {
        instructions = 'Chrome : Cliquez sur l\'icône caméra dans la barre d\'adresse et sélectionnez "Toujours autoriser".';
      } else if (userAgent.includes('Firefox')) {
        instructions = 'Firefox : Cliquez sur l\'icône caméra à gauche de l\'URL et activez les autorisations.';
      } else if (userAgent.includes('Safari')) {
        instructions = 'Safari : Allez dans Préférences > Sites web > Caméra et autorisez ce site.';
      } else {
        instructions = 'Veuillez autoriser l\'accès à la caméra dans les paramètres de votre navigateur pour ce site.';
      }
    }

    setError(`Permission caméra requise: ${instructions}`);
  };

  return (
    <AppLayout>
      <AppBar position="sticky" elevation={0} sx={{ backgroundColor: '#641835' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <Close />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Ajouter une bouteille
          </Typography>
          <IconButton
            color="inherit"
            onClick={handleScanQRCode}
            disabled={isScanning}
          >
            <QrCodeScanner />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {isScanning ? (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ position: 'relative', height: 400 }}>
                <div 
                  id="qr-scanner" 
                  ref={scannerRef} 
                  style={{ 
                    width: '100%', 
                    height: '100%',
                    backgroundColor: '#000'
                  }} 
                />
                <IconButton
                  onClick={stopScanning}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    backgroundColor: 'rgba(255,0,0,0.7)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255,0,0,0.9)',
                    },
                  }}
                >
                  <Close />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* QR Tag Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                {formData.reference ? (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                      <Typography variant="subtitle1" color="success.main" fontWeight="600">
                        Tag scanné
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="success.main">
                      Tag QR validé avec succès
                    </Typography>
                  </Box>
                ) : null}
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mb: 2 }}
                >
                  {formData.reference 
                    ? 'Tag QR validé avec succès'
                    : 'Scannez votre tag QR Vinosk pour commencer'
                  }
                </Typography>
                
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={formData.reference ? <QrCodeScanner /> : <QrCodeScanner />}
                  onClick={handleScanQRCode}
                  sx={{
                    backgroundColor: formData.reference ? 'success.main' : '#641835',
                    color: 'white',
                    py: 2.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: formData.reference ? 'success.dark' : '#8B2942',
                    },
                  }}
                >
                  {formData.reference ? 'Scanner un autre tag' : 'Scanner le tag QR'}
                </Button>


                {formErrors.reference && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {formErrors.reference}
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Image Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Photo de la bouteille
                </Typography>
                
                <Box
                  sx={{
                    height: 200,
                    border: '2px dashed #ccc',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    backgroundColor: '#f5f5f5',
                    cursor: 'pointer',
                    overflow: 'hidden',
                  }}
                  onClick={() => !imagePreview && fileInputRef.current?.click()}
                >
                  {imagePreview ? (
                    <>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage();
                        }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'error.main',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'error.dark',
                          },
                        }}
                      >
                        <Close />
                      </IconButton>
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <CameraAlt sx={{ fontSize: 48, color: 'text.secondary' }} />
                      <Typography color="text.secondary" sx={{ mt: 1 }}>
                        Ajouter une photo
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Optionnel
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                {imagePreview && (
                  <>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<CameraAlt />}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ mt: 2 }}
                    >
                      Changer la photo
                    </Button>
                  </>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleImageSelect}
                />
              </CardContent>
            </Card>

            {/* Form Fields */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth error={!!formErrors.type}>
                    <InputLabel>Type de vin</InputLabel>
                    <Select
                      value={formData.type}
                      label="Type de vin"
                      onChange={(e) => handleInputChange('type', e.target.value)}
                    >
                      {Object.entries(BOTTLE_TYPES_LABELS).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.type && (
                      <Typography color="error" variant="caption">
                        {formErrors.type}
                      </Typography>
                    )}
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Nom du domaine"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                  />

                  <TextField
                    fullWidth
                    label="Année"
                    value={formData.year}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Permettre seulement les chiffres
                      if (/^\d*$/.test(value)) {
                        handleInputChange('year', value);
                      }
                    }}
                    error={!!formErrors.year}
                    helperText={formErrors.year}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Rating Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Ma note
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Rating
                    value={rating}
                    onChange={(_, value) => setRating(value || 0)}
                    size="large"
                    sx={{
                      '& .MuiRating-icon': {
                        color: '#641835',
                      },
                    }}
                  />
                  <Typography variant="body1" color="text.secondary">
                    {rating}/5
                  </Typography>
                  <IconButton
                    onClick={() => setRating(0)}
                    size="small"
                    sx={{ ml: 'auto' }}
                  >
                    <Refresh />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>

            {/* Options */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isGift}
                      onChange={(e) => handleInputChange('isGift', e.target.checked)}
                      sx={{ color: '#641835', '&.Mui-checked': { color: '#641835' } }}
                    />
                  }
                  label="C'est un cadeau"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isPrivate}
                      onChange={(e) => handleInputChange('isPrivate', e.target.checked)}
                      sx={{ color: '#641835', '&.Mui-checked': { color: '#641835' } }}
                    />
                  }
                  label="Mode privé (ne pas partager)"
                />
              </CardContent>
            </Card>

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={isLoading}
              sx={{
                backgroundColor: '#641835',
                color: 'white',
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: '#8B2942',
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                },
              }}
              startIcon={imagePreview && <CameraAlt />}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  {imagePreview ? 'Création avec photo...' : 'Création...'}
                </Box>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </>
        )}
      </Container>


      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </AppLayout>
  );
};